import { useState, useCallback } from "react";
import { DownloadInfo, GameMessage } from "../types";
import { DownloadService } from "../services/DownloadService";
import { extractBlobNameFromUrl } from "../utils";
import logger from "../Log";

export const useDownloadManager = () => {
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState<DownloadInfo | null>(null);
  const [isLoadingAssetInfo, setIsLoadingAssetInfo] = useState(false);

  const handleAssetDownloadByObjectId = useCallback(
    async (objectId: string) => {
      await DownloadService.getSasUrlByObjectIdAndDownload(objectId);
    },
    []
  );

  const handleAssetDownload = useCallback(
    async (containerName: string, blobName: string) => {
      await DownloadService.getSasUrlAndDownload(containerName, blobName);
    },
    []
  );

  const handleConfirmDownload = useCallback(async () => {
    if (downloadInfo) {
      setShowDownloadDialog(false);

      // Prioritize objectId approach if available
      if (downloadInfo.objectId) {
        await handleAssetDownloadByObjectId(downloadInfo.objectId);
      } else {
        // Fallback to old approach
        await handleAssetDownload(
          downloadInfo.containerName,
          downloadInfo.blobName
        );
      }

      setDownloadInfo(null);
    }
  }, [downloadInfo, handleAssetDownloadByObjectId, handleAssetDownload]);

  const handleCancelDownload = useCallback(() => {
    setShowDownloadDialog(false);
    setDownloadInfo(null);
  }, []);

  const processDownloadMessage = useCallback(async (message: GameMessage) => {
    if (
      message.type === "object_interaction" &&
      message.action === "download_asset"
    ) {
      const payload = message.payload;

      // Handle new objectId approach (objectId is at root level)
      if (message.objectId) {
        logger.info(
          `Processing download request with objectId: ${message.objectId}`
        );

        try {
          // Show loading state while fetching asset info
          setIsLoadingAssetInfo(true);

          // Call API first to get asset information
          const assetData = await DownloadService.getAssetInfoByObjectId(
            message.objectId
          );

          logger.info(`Asset info received:`, assetData.assetInfo);

          setDownloadInfo({
            objectId: message.objectId,
            containerName: assetData.assetInfo.containerId,
            blobName: assetData.assetInfo.fileName,
            originalBlobName: assetData.assetInfo.blobName,
            fileName: assetData.assetInfo.fileName,
          });
          setShowDownloadDialog(true);
        } catch (error) {
          logger.error(
            "Failed to get asset info for objectId:",
            message.objectId,
            error
          );
          // You might want to show an error message to the user here
        } finally {
          setIsLoadingAssetInfo(false);
        }

        // Handle legacy containerId + blobName approach (for backward compatibility)
      } else if (payload && payload.containerId && payload.blobName) {
        let blobName = payload.blobName;
        const originalBlobName = payload.blobName;

        if (blobName.startsWith("http")) {
          blobName = extractBlobNameFromUrl(blobName);
        }

        logger.info(
          `Processing download request - Container: ${payload.containerId}, Blob: ${blobName}`
        );

        setDownloadInfo({
          objectId: "", // Empty for legacy approach
          containerName: payload.containerId,
          blobName,
          originalBlobName,
        });
        setShowDownloadDialog(true);
      } else {
        logger.error("Invalid payload for download_asset action", payload);
      }
    }
  }, []);

  return {
    showDownloadDialog,
    downloadInfo,
    isLoadingAssetInfo,
    handleConfirmDownload,
    handleCancelDownload,
    processDownloadMessage,
  };
};
